import { useCallback, useEffect } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useTraitsActions } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { SITE_USER } from '@atlassian/jira-traits/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsJiraNewUserTraitSelector } from './useIsJiraNewUserTraitSelector.tsx';

interface YellowBrickRoad {
	shouldDisplayMessage: boolean;
	// TODO will be implemented in https://hello.jira.atlassian.cloud/browse/PACK-278
	messageId?: string;
}

export const useYbrFeature = () => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const { fetchTraits } = useTraitsActions();
	useEffect(() => {
		// For productioniosation, we will target all users and not just new users so no need to fetch site_user trait to detect new users
		if (cloudId && accountId && !fg('productionise_ybr_cleanup_non_xflow_messages')) {
			fetchTraits([SITE_USER], { cloudId, accountId });
		}
	}, [cloudId, accountId, fetchTraits]);

	// For productioniosation, we will target all users and not just new users
	const [isTargetedUser] = fg('productionise_ybr_cleanup_non_xflow_messages')
		? [true]
		: // eslint-disable-next-line  react-hooks/rules-of-hooks
			useIsJiraNewUserTraitSelector();

	const shouldShowYbrMessage = useCallback(
		({ shouldDisplayMessage }: YellowBrickRoad): boolean => {
			// Prevent SSR for showing the message first, and then hide it due to the experiment cohort
			if (__SERVER__) {
				return false;
			}

			// If the user is not a targeted user or the message is not enabled, we should return the original condition of the message
			if (!shouldDisplayMessage || !isTargetedUser) {
				return shouldDisplayMessage;
			}

			// we should not show the message if the experiment is enabled
			return !expVal('jira_ybr_first_batch', 'isYBREnabled', false);
		},
		[isTargetedUser],
	);

	const shouldShowYbrMessageForIssueView = useCallback(
		({ shouldDisplayMessage }: YellowBrickRoad): boolean => {
			// Prevent SSR for showing the message first, and then hide it due to the experiment cohort
			if (__SERVER__) {
				return false;
			}

			// If the user is not a targeted user or the message is not enabled, we should return the original condition of the message
			if (!shouldDisplayMessage || !isTargetedUser) {
				return shouldDisplayMessage;
			}

			// we should not show the message if the experiment is enabled
			return !expVal('jira_ybr_first_batch', 'isYBREnabled', false);
		},
		[isTargetedUser],
	);

	return {
		shouldShowYbrMessage,
		shouldShowYbrMessageForIssueView,
	};
};
